'use client';

/**
 * MarketMovers — ZONE 2, the HERO of the chain market page (market-v2 spec,
 * docs/design/market-prototype/market-v2.html).
 *
 * Number-first mover tiles: big price, 7d sparkline (crypto_price_history — the
 * same docs the tape already fetches), colored 24h % — each carrying ONE line of
 * narrative from OUR record: the asset's fresh (<12h) recon read, clipped to its
 * why-sentence and doored into the entity dossier. This is the page's "newest
 * conclusion" in market-native form — we know WHY the number moved because the
 * record is ours.
 *
 * Honesty rules:
 *   - the why-line is a verbatim clip of the produced insight, dateline included;
 *   - a mover with NO fresh produced story renders number-only and visibly smaller
 *     (never an invented narrative);
 *   - no movers at all → the zone renders nothing (never an empty box).
 *
 * Picks are the pulse's own day metrics (top gainer / top loser / volume leader)
 * + the chain lens's top NFT by 24h volume — the desk's picks, not ours.
 */

import React, { useMemo, useState, useEffect } from 'react';
import Link from 'next/link';
import { getTimeAgo } from '@/hooks/useMarketAnalysis';
import { useMultiplePriceHistory } from '@/hooks/usePriceHistory';
import TokenSparkline from '@/components/market/TokenSparkline';
import { MarketBandRule } from '@/components/market/market-shared';
import { entityUrl } from '@/lib/entity-url';

// ── tile view model (built by MarketTerminal from pulse + insights + stats) ────────
export interface MoverTileModel {
  key: string;
  /** Display symbol / collection name (already uppercased by the builder). */
  symbol: string;
  /** Role chip, e.g. "TOP GAINER" / "NFT · VOL LEADER". */
  role: string;
  /** Formatted price ("$7.63" / "0.123"). */
  priceLabel: string;
  /** Unit suffix printed small ("ETH floor") — NFT tiles. */
  unit?: string;
  /** 24h move (colored). Omitted when the feed has no change data. */
  change24h?: number;
  /** Secondary stat line for tiles without a sparkline ("2.89 ETH · 26 sales"). */
  sub?: string;
  /** ONE line clipped from the asset's fresh recon read. Absent → bare tile. */
  why?: string;
  /** Insight doc timestamp (the why-line's dateline). */
  whyTimestamp?: string;
  /** Entity dossier slug (insight `socialContext.entitySlug`) — the tile's door. */
  entitySlug?: string;
  /** Token symbol to fetch the 7d sparkline for (tokens only). */
  sparkSymbol?: string;
}

function MoverTile({
  tile,
  prices,
  isUp,
  hasMounted,
}: {
  tile: MoverTileModel;
  prices?: number[];
  isUp?: boolean;
  hasMounted: boolean;
}) {
  const pos = (tile.change24h ?? 0) >= 0;
  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 truncate font-mono text-[13px] font-bold text-gray-900 dark:text-parchment">
          {tile.symbol}
        </span>
        <span className="shrink-0 border-[0.5px] border-gray-200 dark:border-divider px-1.5 py-px font-mono text-[8px] uppercase tracking-[0.12em] text-slate-ds">
          {tile.role}
        </span>
      </div>

      <div className="flex items-end justify-between gap-2.5">
        <span className="font-mono text-[21px] font-bold leading-none tracking-tight text-gray-900 dark:text-parchment">
          {tile.priceLabel}
          {tile.unit && (
            <span className="ml-1 text-[11px] font-semibold text-gray-500 dark:text-parchment-dim">{tile.unit}</span>
          )}
        </span>
        {tile.sparkSymbol && prices && prices.length >= 2 && (
          <TokenSparkline
            symbol={tile.sparkSymbol}
            prices={prices}
            isUp={isUp}
            width={84}
            height={26}
            showGradient={false}
            className="shrink-0"
          />
        )}
        {tile.change24h !== undefined ? (
          <span className={`shrink-0 font-mono text-[13px] font-bold ${pos ? 'text-signal-green dark:text-signal-green-text' : 'text-alert-red dark:text-alert-red-text'}`}>
            {pos ? '+' : '−'}{Math.abs(tile.change24h).toFixed(1)}%
          </span>
        ) : null}
      </div>

      {/* Secondary stat line (e.g. the NFT vol-leader's "2.89 ETH vol 24h"). */}
      {tile.sub && (
        <span className="font-mono text-[9.5px] uppercase tracking-[0.06em] text-slate-ds">{tile.sub}</span>
      )}

      {tile.why && (
        <p
          className="font-mono text-[11px] leading-relaxed text-gray-600 dark:text-parchment/75"
          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {tile.why}
        </p>
      )}

      <div className="mt-auto flex items-baseline justify-between gap-2 font-mono text-[8.5px] uppercase tracking-[0.06em] text-slate-ds">
        {tile.why ? (
          <span>{hasMounted && tile.whyTimestamp ? <>Desk read · {getTimeAgo(tile.whyTimestamp)}</> : 'Desk read'}</span>
        ) : (
          <span>No desk read yet</span>
        )}
        {tile.entitySlug && <span className="text-amber-600 dark:text-scout-gold">Dossier →</span>}
      </div>
    </>
  );

  // Tiles WITH a produced story carry full weight; bare tiles sit smaller/quieter.
  const cls = `flex flex-col border-[0.5px] border-gray-200 dark:border-divider bg-white dark:bg-surface text-left transition-colors ${
    tile.why ? 'gap-2 p-3.5' : 'gap-1.5 p-2.5 opacity-80'
  } ${tile.entitySlug ? 'hover:border-amber-400/50 dark:hover:border-scout-gold/40' : ''}`;

  return tile.entitySlug ? (
    <Link href={entityUrl(tile.entitySlug, { hash: 'market' })} className={cls}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

interface MarketMoversProps {
  tiles: MoverTileModel[];
  loading: boolean;
}

export default function MarketMovers({ tiles, loading }: MarketMoversProps) {
  // Relative datelines compare against the wall clock — render post-mount only
  // (SSR/client HTML must agree).
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  // 7d sparkline batch — same source the tape uses (crypto_price_history).
  const sparkSymbols = useMemo(
    () => tiles.filter((t) => t.sparkSymbol).map((t) => t.sparkSymbol as string),
    [tiles],
  );
  const historyMap = useMultiplePriceHistory(sparkSymbols);

  // Quiet state: nothing to show and nothing loading → no zone at all.
  if (!loading && tiles.length === 0) return null;

  // Tiles are bounded by construction (≤4: top gainer / top loser / vol leader /
  // top NFT, deduped) — the grid never needs to scroll or truncate.
  const withWhy = tiles.filter((t) => t.why).length;
  const freshLine =
    tiles.length === 0
      ? undefined
      : withWhy === tiles.length
        ? tiles.length === 1
          ? 'fresh desk read attached'
          : `all ${tiles.length} carry a fresh read`
        : `${withWhy} of ${tiles.length} carry a fresh read`;

  return (
    <div className="pt-5">
      <MarketBandRule
        label="◆ Movers · the desk's why"
        right={freshLine}
      />
      <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        {tiles.length === 0
          ? [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-28 animate-pulse border-[0.5px] border-gray-200 dark:border-divider bg-gray-50 dark:bg-surface"
                aria-label="Loading mover"
              />
            ))
          : tiles.map((tile) => {
              const hist = tile.sparkSymbol
                ? historyMap.get(tile.sparkSymbol.toUpperCase())
                : undefined;
              return (
                <MoverTile
                  key={tile.key}
                  tile={tile}
                  prices={hist?.prices}
                  isUp={hist?.isUp}
                  hasMounted={hasMounted}
                />
              );
            })}
      </div>
    </div>
  );
}
